import LiveActivityFeed from '@/components/LiveActivityFeed'
import SocialProofBanner from '@/components/SocialProofBanner'
import SuccessStats from '@/components/SuccessStats'
import TestimonialCarousel from '@/components/TestimonialCarousel'
import UserCountBadge from '@/components/UserCountBadge'
import Features from '@app-studio-web/ui/components/Features'
import FrequentlyAskedQuestions from '@app-studio-web/ui/components/FrequentlyAskedQuestions'
import LimitedTimeOfferBanner from '@app-studio-web/ui/components/LimitedTimeOfferBanner'
import LimitedTimeOfferDialog from '@app-studio-web/ui/components/LimitedTimeOfferDialog'
import MoneyBackGuarantee from '@app-studio-web/ui/components/MoneyBackGuarantee'
import Plans from '@app-studio-web/ui/components/Plans'
import Trust from '@app-studio-web/ui/components/Trust'
import type { GetPricesData } from '@app-studio-web/ui/loaders/getPricesLoader'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import LoadingPage from '@app-studio-web/ui/pages/LoadingPage'
import type { Plan } from '@app-studio-web/ui/types'
import { Suspense, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Await, useLoaderData } from 'react-router-dom'

interface Props {
  hidePerks?: boolean
  subscriptionPlanOrder?: Array<'top' | 'first' | 'second'>
}

export default function PlansPage({
  hidePerks = false,
  subscriptionPlanOrder,
}: Props = {}) {
  const [countdownUntil, setCountdownUntil] = useState<number>(0)
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan>('top')
  const { t } = useTranslation()

  const firstPerk = hidePerks
    ? undefined
    : t('pages.checkout.plans.realtimeSpeakingPerk')

  const data = useLoaderData() as GetPricesData

  useEffect(() => {
    const dialogTimeout = setTimeout(() => {
      setIsDialogOpen(true)
    }, 30000)

    setCountdownUntil(Date.now() + 9 * 60 * 1000)

    return () => {
      clearTimeout(dialogTimeout)
    }
  }, [])

  if (isSubmitting) return <LoadingPage />

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data}>
        {(getPricesData: GetPricesData) => (
          <div>
            <LimitedTimeOfferBanner
              countdownUntil={countdownUntil}
              currency={getPricesData.currency}
              isSubmitting={isSubmitting}
              plan={selectedPlan}
              priceId={
                selectedPlan === 'one-time'
                  ? getPricesData.oneTimePrice?.id
                  : selectedPlan === 'top'
                    ? getPricesData.topIntroPrice?.id
                    : selectedPlan === 'first'
                      ? getPricesData.firstIntroPrice?.id
                      : getPricesData.secondIntroPrice?.id
              }
              setIsSubmitting={setIsSubmitting}
              stripePriceFull={
                selectedPlan === 'one-time'
                  ? getPricesData.oneTimePrice
                  : selectedPlan === 'top'
                    ? getPricesData.topPrice
                    : selectedPlan === 'first'
                      ? getPricesData.firstPrice
                      : getPricesData.secondPrice
              }
            />

            <main className="custom-container pt-8 pb-14">
              <h1 className="custom-heading mb-4">
                <Trans
                  components={{
                    colored: <span className="text-primary" />,
                  }}
                  i18nKey="pages.checkout.plans.heading"
                />
              </h1>

              <SocialProofBanner />

              <UserCountBadge />

              <section className="mt-4 mb-4">
                <LiveActivityFeed />
              </section>

              <section className="mb-6">
                <SuccessStats />
              </section>

              <p className="text-primary mb-4 text-center text-sm font-semibold">
                {t('pages.checkout.viewingNow')}
              </p>

              <section className="mb-10">
                <Plans
                  countdownUntil={countdownUntil}
                  currency={getPricesData.currency}
                  firstIntroPrice={getPricesData.firstIntroPrice}
                  firstPerk={firstPerk}
                  firstPrice={getPricesData.firstPrice}
                  isSubmitting={isSubmitting}
                  oneTimePrice={getPricesData.oneTimePrice}
                  onPlanChange={setSelectedPlan}
                  secondIntroPrice={getPricesData.secondIntroPrice}
                  secondPrice={getPricesData.secondPrice}
                  selectedPlan={selectedPlan}
                  setIsSubmitting={setIsSubmitting}
                  subscriptionPlanOrder={subscriptionPlanOrder}
                  topIntroPrice={getPricesData.topIntroPrice}
                  topPrice={getPricesData.topPrice}
                />
              </section>

              <section className="mb-12">
                <TestimonialCarousel />
              </section>

              <section className="mb-12">
                <Trust />
              </section>

              <section className="mb-12">
                <Features />
              </section>

              <section className="mb-12">
                <MoneyBackGuarantee />
              </section>

              <section className="mb-12">
                <FrequentlyAskedQuestions />
              </section>

              <section className="mb-16">
                <Plans
                  countdownUntil={countdownUntil}
                  currency={getPricesData.currency}
                  firstIntroPrice={getPricesData.firstIntroPrice}
                  firstPerk={firstPerk}
                  firstPrice={getPricesData.firstPrice}
                  isSubmitting={isSubmitting}
                  oneTimePrice={getPricesData.oneTimePrice}
                  onPlanChange={setSelectedPlan}
                  secondIntroPrice={getPricesData.secondIntroPrice}
                  secondPrice={getPricesData.secondPrice}
                  selectedPlan={selectedPlan}
                  setIsSubmitting={setIsSubmitting}
                  subscriptionPlanOrder={subscriptionPlanOrder}
                  topIntroPrice={getPricesData.topIntroPrice}
                  topPrice={getPricesData.topPrice}
                />
              </section>
            </main>

            <LimitedTimeOfferDialog
              currency={getPricesData.currency}
              isSubmitting={isSubmitting}
              onClose={() => setIsDialogOpen(false)}
              open={isDialogOpen}
              plan={selectedPlan}
              setIsSubmitting={setIsSubmitting}
              stripePriceFull={
                selectedPlan === 'one-time'
                  ? getPricesData.oneTimePrice
                  : selectedPlan === 'top'
                    ? getPricesData.topPrice
                    : selectedPlan === 'first'
                      ? getPricesData.firstPrice
                      : getPricesData.secondPrice
              }
              stripePriceIntro={
                selectedPlan === 'one-time'
                  ? undefined
                  : selectedPlan === 'top'
                    ? getPricesData.topIntroPrice
                    : selectedPlan === 'first'
                      ? getPricesData.firstIntroPrice
                      : getPricesData.secondIntroPrice
              }
            />
          </div>
        )}
      </Await>
    </Suspense>
  )
}
